import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { makeStyles, useTheme, withStyles } from "@mui/styles";
import {
  Toolbar,
  AppBar,
  Button,
  IconButton,
  Box,
  Hidden,
  Divider,
  Collapse,
  Badge,
  useMediaQuery,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuLucide, X as XLucide, Heart, Search as SearchLucide } from "lucide-react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WorkOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
// import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  usePopupState,
  bindHover,
  bindMenu,
} from "material-ui-popup-state/hooks";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import Logo from "icons/new-logo.svg";
import LogoWhite from "icons/logoWhite.svg";
import SelectLanguage from "./SelectLanguage";
import { FormattedMessage } from "react-intl";
import sh from "bundles/common/utils/sh";
import engagementTracker from "bundles/Recruiter/engagement/engagementTracker";
import ProfilePicture from "../common/components/ProfilePicture";
import {
  desktopAuthenticatedSubmenus,
  mobileAuthenticatedSubmenus,
} from "bundles/common/utils/recruiters/navbar";

export default function MainAppBarRecruiter({ children }) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const theme = useTheme();
  const useStyles = makeStyles((theme) => ({
    root: {
      flexGrow: 0,
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1100,
    },
    logo: {
      flexGrow: 1,
    },
    menuButton: {
      marginRight: theme.spacing(2),
      color: "hsl(178 93% 10%)",
      textTransform: "none",
    },
    drawer: {
      width: "240px !important",
      borderTopRightRadius: "30px",
      borderBottomRightRadius: "30px",
    },
    drawerHeader: {
      height: "100px",
      color: theme.palette.common.white,
      backgroundColor: theme.palette.secondary.main,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    rightHeader: {
      marginRight: "16px",
    },
    headerTitle: {
      fontWeight: 600,
    },
    drawerItem: {
      justifyContent: "left",
      columnGap: "4px",
    },
    divider: {
      margin: "8px 16px !important",
      fontWeight: "bolder !important",
    },
    PrimaryButton: {
      marginLeft: "16px !important",
    },
  }));
  const classes = useStyles();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenus, setSubmenus] = useState(
    matches ? desktopAuthenticatedSubmenus : mobileAuthenticatedSubmenus
  );

  const publicNavLinks = [
    {
      id: "menu.pricing",
      href: `/${i18nLocale}/recruiters#pricing`,
    },
    {
      id: "menu.why_us",
      href: `/${i18nLocale}/recruiters/why-us`,
    },
    {
      id: "blog.tips_and_actualities",
      href: `/${i18nLocale}/recruiters/posts`,
    },
  ];
  const [submenusUpdateCounter, setSubmenusUpdateCounter] = useState(0);
  const desktopNavButtonSx = {
    textTransform: "none",
    fontFamily: "Segoe UI Regular, sans-serif",
    borderRadius: "10px",
    px: 1.75,
    py: 0.75,
    fontSize: "0.95rem",
    color: "hsl(178 93% 10%)",
    "&:hover": {
      backgroundColor: "hsl(153 68% 25% / 0.08)",
      color: "hsl(153 68% 25%)",
    },
  };

  const handleSubmenuClicked = (submenu) => {
    let tempSubmenus = submenus;
    let tempSubmenu = tempSubmenus.find((s) => s.code == submenu.code);
    tempSubmenus.forEach((s) => {
      if (s.code != submenu.code) s.isOpen = false;
    });
    tempSubmenu.isOpen = !tempSubmenu.isOpen;
    setSubmenus(tempSubmenus);
    setSubmenusUpdateCounter(submenusUpdateCounter + 1);
  };

  const handleRedirectClick = (link, popupState = null) => {
    if (link.logout) return logout();

    if (matches && popupState) {
      popupState.close();
    } else {
      setIsDrawerOpen(false);
    }

    let redirectUrl = `/${i18nLocale}/${link.target}`;
    if (link.search) redirectUrl += link.search;
    window.location.href = redirectUrl;
  };

  const LogoIcon = (props) => {
    return (
      <MuiLink
        href={currentRecruiter ? `/${i18nLocale}/recruiter` : `/${i18nLocale}/`}
      >
        <Box
          component="img"
          id="logo-squarehub"
          alt="Squarehub"
          width="100%"
          height="auto"
          maxWidth={200}
          {...props}
          src={props.white ? LogoWhite : Logo}
        />
      </MuiLink>
    );
  };

  const logout = (callback) => {
    engagementTracker.endAll("logged_out");
    sh.delete(`/${i18nLocale}/recruiters/sign_out`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token,
      },
    })
      .then(() => {
        if (callback && typeof callback == "function") callback();
        if (window) window.location.replace("/");
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {});
  };

  const CvThequeDropdown = () => {
    const cvThequePopup = usePopupState({
      variant: "popover",
      popupId: "cvThequeMenu",
    });

    const dropdownItemSx = {
      py: "0.625rem",
      px: "1rem",
      borderRadius: "0.5rem",
      mx: "0.5rem",
      gap: "0.625rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      color: "hsl(178 93% 10%)",
      transition: "all 150ms ease",
      "&:hover": {
        backgroundColor: "hsl(153 68% 25% / 0.08)",
        color: "hsl(153 68% 25%)",
      },
    };

    const dropdownIconSx = {
      fontSize: "1.125rem",
      color: "inherit",
      opacity: 0.6,
    };

    return (
      <Box>
        <Button
          size="small"
          variant="text"
          className={classes.menuButton}
          sx={desktopNavButtonSx}
          {...bindHover(cvThequePopup)}
        >
          <Badge
            badgeContent={currentRecruiter?.favorite_candidate_ids?.length || 0}
            max={999}
            invisible={!(currentRecruiter?.favorite_candidate_ids?.length > 0)}
            sx={{
              marginRight: "8px",
              "& .MuiBadge-badge": {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.common.white,
              },
            }}
          >
            <Typography variant="body2" sx={{ color: "inherit" }}>
              <FormattedMessage id="job_offer.cv_theque" />
            </Typography>
          </Badge>
          <ExpandMoreIcon sx={{ color: "hsl(178 93% 10%)", fontSize: "1rem", ml: 0.5 }} />
        </Button>
        <HoverMenu
          {...bindMenu(cvThequePopup)}
          disableScrollLock
          MenuListProps={{
            dense: true,
            sx: {
              py: 0.5,
              minWidth: 220,
            },
          }}
          PaperProps={{
            onMouseLeave: cvThequePopup.close,
            sx: {
              minWidth: 220,
              overflow: "hidden",
            },
          }}
        >
          <MenuItem
            sx={dropdownItemSx}
            onClick={() => {
              cvThequePopup.close();
              window.location.href = `/${i18nLocale}/recruiter?tab=cv_theque&onlyFavorites=true`;
            }}
          >
            <Heart size={16} style={dropdownIconSx} />
            <FormattedMessage id="nav.recruiter.favorite_candidates" />
          </MenuItem>
          <MenuItem
            sx={dropdownItemSx}
            onClick={() => {
              cvThequePopup.close();
              window.location.href = `/${i18nLocale}/recruiter?tab=cv_theque`;
            }}
          >
            <SearchLucide size={16} style={dropdownIconSx} />
            <FormattedMessage id="nav.recruiter.find_candidate" />
          </MenuItem>
        </HoverMenu>
      </Box>
    );
  };

  const UserMenu = ({ classes }) => {
    const popupState = usePopupState({
      variant: "popover",
      popupId: "demoMenu",
    });

    const menuItemSx = {
      py: "0.625rem",
      px: "1rem",
      borderRadius: "0.5rem",
      mx: "0.5rem",
      gap: "0.625rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      color: "hsl(178 93% 10%)",
      transition: "all 150ms ease",
      "& .MuiTypography-root": { color: "inherit" },
      "&:hover": {
        backgroundColor: "hsl(153 68% 25% / 0.08)",
        color: "hsl(153 68% 25%)",
        "& .MuiSvgIcon-root, & svg": {
          color: "hsl(153 68% 25%)",
          opacity: 1,
        },
      },
    };

    const menuIconSx = {
      fontSize: "1.125rem",
      color: "inherit",
      opacity: 0.6,
    };

    return (
      <Box>
        <IconButton
          disableRipple={true}
          className={classes.iconButton}
          sx={{ color: "hsl(178 93% 10%)" }}
          {...bindHover(popupState)}
        >
          <ProfilePicture
            rounded
            picture={currentRecruiter?.company?.logo_url}
            initials={
              <Typography sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                {currentRecruiter?.first_name[0]}
                {currentRecruiter?.last_name ? currentRecruiter?.last_name[0] : ""}
              </Typography>
            }
          />{" "}
          <ExpandMoreIcon sx={{ color: "hsl(178 93% 10%)", fontSize: "1.25rem" }} />
        </IconButton>
        <HoverMenu
          {...bindMenu(popupState)}
          disableScrollLock
          MenuListProps={{
            dense: true,
            sx: {
              py: 0.5,
              px: 0,
              width: "100%",
              minWidth: 240,
              color: "hsl(178 93% 10%)",
              "& .MuiMenuItem-root.Mui-focusVisible": {
                backgroundColor: "rgba(22, 109, 76, 0.14)",
              },
            },
          }}
          PaperProps={{
            onMouseLeave: popupState.close,
            sx: {
              minWidth: 240,
              maxWidth: 280,
              overflow: "hidden",
              "& .MuiMenuItem-root": { color: "hsl(178 93% 10%)" },
            },
          }}
        >
          <MenuItem
            sx={menuItemSx}
            onClick={() => handleRedirectClick({ target: "" }, popupState)}
          >
            <HomeOutlinedIcon sx={menuIconSx} />
            <FormattedMessage id="menu.home" />
          </MenuItem>
          <MenuItem
            sx={menuItemSx}
            onClick={() =>
              handleRedirectClick({ target: "recruiter/settings" }, popupState)
            }
          >
            <SettingsOutlinedIcon sx={menuIconSx} />
            <FormattedMessage id="menu.settings" />
          </MenuItem>
          <Divider className={classes.divider} />
          {desktopAuthenticatedSubmenus.map((submenu) => {
            return (
              <Box key={`key-${submenu.titleId}`}>
                <Typography
                  variant="body2bold"
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    color: "hsl(178 93% 10%)",
                  }}
                >
                  <FormattedMessage id={submenu.titleId} />
                </Typography>
                <Box style={{ marginTop: "8px" }}>
                  {submenu.links.map((link) => {
                    return (
                      <MenuItem
                        key={link.titleId}
                        sx={menuItemSx}
                        onClick={() => {
                          handleRedirectClick(link, popupState);
                        }}
                      >
                        {link.icon && (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              "& svg": {
                                fontSize: "1.125rem",
                                opacity: 0.6,
                              },
                            }}
                          >
                            {link.icon()}
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ ml: link.icon ? 1 : 0 }}
                        >
                          <FormattedMessage id={link.titleId} />
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Box>
                <Divider className={classes.divider} />
              </Box>
            );
          })}
          <Box sx={{ mt: 1.5, mb: 1.5, px: 1 }}>
            <SelectLanguage initLocale={i18nLocale} inline compact />
          </Box>

          <Divider sx={{ mx: "1rem", my: "0.375rem", borderColor: "hsl(220 13% 91%)" }} />

          {currentRecruiter && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                px: 2,
                py: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={logout}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  borderColor: "hsl(153 68% 25%)",
                  color: "hsl(153 68% 25%)",
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 2.75,
                  py: 0.85,
                  minWidth: "min(100%, 200px)",
                  boxSizing: "border-box",
                  "&:hover": {
                    borderColor: "hsl(153 68% 22%)",
                    color: "hsl(153 68% 22%)",
                    backgroundColor: "hsl(153 68% 25% / 0.08)",
                  },
                }}
              >
                <FormattedMessage id="logout" />
              </Button>
            </Box>
          )}
        </HoverMenu>
      </Box>
    );
  };

  const CustomUserMenu = withStyles(() => ({
    iconButton: {
      "&:hover": {
        backgroundColor: "unset",
        opacity: 0.84,
      },
    },
    divider: {
      margin: "8px 0 !important",
      width: "100%",
    },
  }))(UserMenu);

  const RecruiterAvatar = () => {
    return <CustomUserMenu />;
  };

  useEffect(() => {
    if (!isDrawerOpen && !matches) {
      let menus = mobileAuthenticatedSubmenus.map((menu) => {
        let copy = menu;
        copy.isOpen = false;
        return copy;
      });
      setSubmenus(menus);
    }
  }, [isDrawerOpen]);

  const countPendingApplications =
    currentRecruiter?.company?.kanban_pending_cards_count ??
    currentRecruiter?.company?.new_applications_count;

  return (
    <>
      <div className={classes.root} id="client-sh-header">
        <AppBar
          position="static"
          elevation={0}
          style={{
            height: matches ? "inherit" : "68px",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid rgba(2, 51, 49, 0.08)",
          }}
        >
          <Toolbar style={{ paddingRight: matches ? "16px" : "4px" }}>
            {matches && (
              <Box flexGrow={1}>
                <LogoIcon />
              </Box>
            )}
            <Hidden mdDown>
              {currentRecruiter ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 0.75,
                    rowGap: 1,
                    ml: "auto",
                  }}
                >
                  {currentRecruiter?.has_company_portal && (
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/recruiter/company_portal`}
                      startIcon={<BusinessIcon sx={{ fontSize: "1rem" }} />}
                      sx={desktopNavButtonSx}
                    >
                      <FormattedMessage id="nav.recruiter.back_to_company_selector" />
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    className={classes.menuButton}
                    href={`/${i18nLocale}/recruiter?tab=jobs`}
                    sx={desktopNavButtonSx}
                  >
                    <FormattedMessage id="job_offer.my_job_offers" />
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    className={classes.menuButton}
                    href={`/${i18nLocale}/recruiter?tab=applications`}
                    sx={desktopNavButtonSx}
                  >
                    <Badge
                      badgeContent={countPendingApplications || 0}
                      invisible={!countPendingApplications}
                      sx={{
                        marginRight: "8px",
                        "& .MuiBadge-badge": {
                          backgroundColor: theme.palette.secondary.main,
                          color: theme.palette.common.white,
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "inherit" }}>
                        <FormattedMessage id="job_search.my_jobs" />
                      </Typography>
                    </Badge>
                  </Button>
                  <CvThequeDropdown classes={classes} />
                  <Button
                    size="small"
                    variant="rounded"
                    onClick={() =>
                      handleRedirectClick({
                        target: "recruiter/job_offer_creation",
                      })
                    }
                    sx={{
                      mr: 1,
                      minWidth: "150px",
                      textTransform: "none",
                      px: 1.75,
                      py: 0.75,
                      fontSize: "0.95rem",
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      backgroundColor: "hsl(153 68% 25%)",
                      color: "white",
                      transition: "all 180ms ease",
                      "&:hover": {
                        backgroundColor: "hsl(153 68% 22%)",
                      },
                    }}
                  >
                    <FormattedMessage id="job_offer.post_new_job" />
                  </Button>
                  <RecruiterAvatar />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexGrow: 1,
                      gap: 0.75,
                      pr: 6,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/recruiters#pricing`}
                      sx={{
                        ...desktopNavButtonSx,
                      }}
                    >
                      <FormattedMessage id="menu.pricing" />
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/recruiters/why-us`}
                      sx={{
                        ...desktopNavButtonSx,
                      }}
                    >
                      <FormattedMessage id="menu.why_us" />
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/recruiters/posts`}
                      sx={{
                        ...desktopNavButtonSx,
                      }}
                    >
                      <FormattedMessage id="blog.tips_and_actualities" />
                    </Button>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    href={`/${i18nLocale}`}
                    sx={{
                      ...desktopNavButtonSx,
                      mr: 0.5,
                    }}
                  >
                    <FormattedMessage id="candidate.candidate_space" />
                  </Button>
                  <Button
                    className={classes.PrimaryButton}
                    variant="rounded"
                    size="small"
                    color="secondaryContained"
                    style={{ marginRight: "16px", minWidth: "150px", textTransform: "none" }}
                    href={`/${i18nLocale}/recruiters/sign_in`}
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif !important",
                      fontWeight: "400 !important",
                      textTransform: "none",
                      backgroundColor: "hsl(153 68% 25%) !important",
                      color: "white",
                      transition: "all 180ms ease",
                      "&:hover": {
                        backgroundColor: "hsl(153 68% 22%) !important",
                      },
                    }}
                  >
                    <FormattedMessage id="signin" />
                  </Button>
                  <SelectLanguage initLocale={i18nLocale} />
                </>
              )}
              <Box mx={1} />
            </Hidden>
            <Hidden lgUp>
              <IconButton
                edge="end"
                aria-label="menu"
                sx={{ color: "hsl(178 93% 10%)" }}
                onClick={() => setMobileOpen((prev) => !prev)}
              >
                {mobileOpen ? (
                  <XLucide size={24} />
                ) : (
                  <MenuLucide size={24} />
                )}
              </IconButton>
            </Hidden>
            {!matches && (
              <Box
                flexGrow={1}
                style={{ marginLeft: "-48px", textAlign: "center" }}
              >
                <LogoIcon />
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <AnimatePresence>
          {mobileOpen && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              sx={{
                display: { xs: "block", lg: "none" },
                backgroundColor: "white",
                borderBottom: "1px solid rgba(2, 51, 49, 0.08)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                {currentRecruiter ? (
                  <>
                    {/* Profile header */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1.5, mb: 1, borderBottom: "1px solid rgba(2, 51, 49, 0.08)" }}>
                      <ProfilePicture
                        rounded
                        picture={currentRecruiter?.company?.logo_url}
                        initials={
                          <Typography sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                            {currentRecruiter?.first_name[0]}
                            {currentRecruiter?.last_name ? currentRecruiter?.last_name[0] : ""}
                          </Typography>
                        }
                      />
                      <Box>
                        <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "0.875rem", color: "hsl(178 93% 10%)" }}>
                          {currentRecruiter?.first_name} {currentRecruiter?.last_name}
                        </Typography>
                        <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif", fontSize: "0.75rem", color: "hsl(178 10% 45%)" }}>
                          {currentRecruiter?.company?.name}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Company portal */}
                    {currentRecruiter?.has_company_portal && (
                      <MuiLink
                        href={`/${i18nLocale}/recruiter/company_portal`}
                        underline="none"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 1.2,
                          color: "hsl(178 93% 10%)",
                          fontFamily: "Segoe UI Regular, sans-serif",
                          fontSize: "0.95rem",
                          borderRadius: "10px",
                          px: 1,
                          transition: "all 150ms ease",
                          "&:hover": {
                            backgroundColor: "hsl(153 68% 25% / 0.08)",
                            color: "hsl(153 68% 25%)",
                          },
                        }}
                        onClick={() => setMobileOpen(false)}
                      >
                        <BusinessIcon sx={{ fontSize: "1.125rem", opacity: 0.6 }} />
                        <FormattedMessage id="nav.recruiter.back_to_company_selector" />
                      </MuiLink>
                    )}

                    {/* Main nav links */}
                    <MuiLink
                      href={`/${i18nLocale}/recruiter`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1.2,
                        color: "hsl(178 93% 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif",
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                        px: 1,
                        transition: "all 150ms ease",
                        "&:hover": {
                          backgroundColor: "hsl(153 68% 25% / 0.08)",
                          color: "hsl(153 68% 25%)",
                        },
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      <HomeOutlinedIcon sx={{ fontSize: "1.125rem", opacity: 0.6 }} />
                      <FormattedMessage id="menu.home" />
                    </MuiLink>
                    <MuiLink
                      href={`/${i18nLocale}/recruiter?tab=jobs`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1.2,
                        color: "hsl(178 93% 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif",
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                        px: 1,
                        transition: "all 150ms ease",
                        "&:hover": {
                          backgroundColor: "hsl(153 68% 25% / 0.08)",
                          color: "hsl(153 68% 25%)",
                        },
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      <WorkOutlinedIcon sx={{ fontSize: "1.125rem", opacity: 0.6 }} />
                      <FormattedMessage id="job_offer.my_job_offers" />
                    </MuiLink>
                    <MuiLink
                      href={`/${i18nLocale}/recruiter?tab=applications`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1.2,
                        color: "hsl(178 93% 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif",
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                        px: 1,
                        transition: "all 150ms ease",
                        "&:hover": {
                          backgroundColor: "hsl(153 68% 25% / 0.08)",
                          color: "hsl(153 68% 25%)",
                        },
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      <PeopleOutlineIcon sx={{ fontSize: "1.125rem", opacity: 0.6 }} />
                      <Badge
                        badgeContent={countPendingApplications || 0}
                        invisible={!countPendingApplications}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.common.white,
                          },
                        }}
                      >
                        <Typography sx={{ color: "inherit", fontFamily: "inherit", fontSize: "inherit" }}>
                          <FormattedMessage id="job_search.my_jobs" />
                        </Typography>
                      </Badge>
                    </MuiLink>
                    <MuiLink
                      href={`/${i18nLocale}/recruiter?tab=cv_theque`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1.2,
                        color: "hsl(178 93% 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif",
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                        px: 1,
                        transition: "all 150ms ease",
                        "&:hover": {
                          backgroundColor: "hsl(153 68% 25% / 0.08)",
                          color: "hsl(153 68% 25%)",
                        },
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      <SearchLucide size={18} style={{ opacity: 0.6 }} />
                      <Badge
                        badgeContent={currentRecruiter?.favorite_candidate_ids?.length || 0}
                        invisible={!(currentRecruiter?.favorite_candidate_ids?.length > 0)}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.common.white,
                          },
                        }}
                      >
                        <Typography sx={{ color: "inherit", fontFamily: "inherit", fontSize: "inherit" }}>
                          <FormattedMessage id="job_offer.cv_theque" />
                        </Typography>
                      </Badge>
                    </MuiLink>
                    <MuiLink
                      href={`/${i18nLocale}/recruiter/settings`}
                      underline="none"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1.2,
                        color: "hsl(178 93% 10%)",
                        fontFamily: "Segoe UI Regular, sans-serif",
                        fontSize: "0.95rem",
                        borderRadius: "10px",
                        px: 1,
                        transition: "all 150ms ease",
                        "&:hover": {
                          backgroundColor: "hsl(153 68% 25% / 0.08)",
                          color: "hsl(153 68% 25%)",
                        },
                      }}
                      onClick={() => setMobileOpen(false)}
                    >
                      <SettingsOutlinedIcon sx={{ fontSize: "1.125rem", opacity: 0.6 }} />
                      <FormattedMessage id="menu.settings" />
                    </MuiLink>

                    {/* Submenus */}
                    <Box sx={{ borderTop: "1px solid rgba(2, 51, 49, 0.08)", mt: 1, pt: 1 }}>
                      {submenus.map((submenu) => (
                        <Box key={`mobile-${submenu.code || submenu.titleId}`}>
                          <Box
                            component="button"
                            onClick={() => handleSubmenuClicked(submenu)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              py: 1.2,
                              px: 1,
                              border: "none",
                              bgcolor: "transparent",
                              color: "hsl(178 93% 10%)",
                              fontFamily: "Segoe UI Semibold, sans-serif",
                              fontSize: "0.95rem",
                              borderRadius: "10px",
                              cursor: "pointer",
                              transition: "all 150ms ease",
                              "&:hover": {
                                backgroundColor: "hsl(153 68% 25% / 0.08)",
                                color: "hsl(153 68% 25%)",
                              },
                            }}
                          >
                            <FormattedMessage id={submenu.titleId} />
                            {submenu.isOpen ? <ExpandLessIcon sx={{ fontSize: "1.25rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1.25rem" }} />}
                          </Box>
                          <Collapse in={submenu.isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 1 }}>
                              {submenu.links.map((link) => (
                                <MuiLink
                                  key={link.titleId}
                                  href="#"
                                  underline="none"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 1,
                                    color: "hsl(178 93% 10%)",
                                    fontFamily: "Segoe UI Regular, sans-serif",
                                    fontSize: "0.875rem",
                                    borderRadius: "10px",
                                    px: 2,
                                    transition: "all 150ms ease",
                                    "&:hover": {
                                      backgroundColor: "hsl(153 68% 25% / 0.08)",
                                      color: "hsl(153 68% 25%)",
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setMobileOpen(false);
                                    handleRedirectClick(link);
                                  }}
                                >
                                  {link.icon && (
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", "& svg, & img": { width: 16, height: 16, opacity: 0.6 } }}>
                                      {link.icon()}
                                    </Box>
                                  )}
                                  <FormattedMessage id={link.titleId} />
                                </MuiLink>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </Box>

                    {/* Post new job CTA */}
                    <Box sx={{ borderTop: "1px solid rgba(2, 51, 49, 0.08)", mt: 1, pt: 1.5 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/${i18nLocale}/recruiter/job_offer_creation`}
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          backgroundColor: "hsl(153 68% 25%)",
                          transition: "all 180ms ease",
                          "&:hover": { backgroundColor: "hsl(153 68% 22%)" },
                        }}
                      >
                        <FormattedMessage id="job_offer.post_new_job" />
                      </Button>
                    </Box>

                    {/* Language selector */}
                    <Box sx={{ py: 1, px: 1 }}>
                      <SelectLanguage initLocale={i18nLocale} inline compact />
                    </Box>

                    {/* Logout */}
                    <Box sx={{ borderTop: "1px solid rgba(2, 51, 49, 0.08)", pt: 1.5 }}>
                      <Button
                        variant="outlined"
                        onClick={() => { setMobileOpen(false); logout(); }}
                        fullWidth
                        sx={{
                          maxWidth: 280,
                          mx: "auto",
                          display: "block",
                          textTransform: "none",
                          borderRadius: "999px",
                          borderColor: "hsl(153 68% 25%)",
                          color: "hsl(153 68% 25%)",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          py: 0.85,
                          "&:hover": {
                            borderColor: "hsl(153 68% 22%)",
                            color: "hsl(153 68% 22%)",
                            backgroundColor: "hsl(153 68% 25% / 0.08)",
                          },
                        }}
                      >
                        <FormattedMessage id="logout" />
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    {publicNavLinks.map((link) => (
                      <MuiLink
                        key={link.id}
                        href={link.href}
                        underline="none"
                        sx={{
                          display: "block",
                          py: 1.2,
                          color: "hsl(178 93% 10%)",
                          fontFamily: "Segoe UI Regular, sans-serif",
                          fontSize: "0.95rem",
                          borderRadius: "10px",
                          px: 1,
                          transition: "all 150ms ease",
                          "&:hover": {
                            backgroundColor: "hsl(153 68% 25% / 0.08)",
                            color: "hsl(153 68% 25%)",
                          },
                        }}
                        onClick={() => setMobileOpen(false)}
                      >
                        <FormattedMessage id={link.id} />
                      </MuiLink>
                    ))}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        href={`/${i18nLocale}`}
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          py: 1.2,
                          fontSize: "0.95rem",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          color: "hsl(178 93% 10%)",
                          backgroundColor: "white",
                          border: "1px solid rgba(2, 51, 49, 0.2)",
                          "&:hover": {
                            backgroundColor: "hsl(0 0% 97%)",
                            borderColor: "rgba(2, 51, 49, 0.35)",
                          },
                        }}
                      >
                        <FormattedMessage id="candidate.candidate_space" />
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/${i18nLocale}/recruiters/sign_in`}
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          py: 1.2,
                          fontSize: "0.95rem",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          backgroundColor: "hsl(153 68% 25%)",
                          color: "white",
                          transition: "all 180ms ease",
                          "&:hover": {
                            backgroundColor: "hsl(153 68% 22%)",
                          },
                        }}
                      >
                        <FormattedMessage id="signin" />
                      </Button>
                    </Box>
                    <Box sx={{ py: 1, px: 1 }}>
                      <SelectLanguage initLocale={i18nLocale} fullWidth={true} />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </AnimatePresence>
      </div>
      <div style={{ height: matches ? "64px" : "68px" }} />
      {children}
    </>
  );
}
